/**
 * <bundle-tier-selector> - a set of clickable radio "tier" cards (Buy 1 / Buy 2 /
 * Buy 3 ...) that all add the SAME variant to cart at different quantities.
 *
 * Rather than reimplementing add-to-cart, this drives the page's existing native
 * quantity-selector-component (same one buy-buttons.liquid already renders,
 * kept in the DOM but visually hidden - see bundle-selector.liquid's stylesheet).
 * product-form.js reads that component's live value at submit time, so once we
 * call setValue() here, Add to Cart just works with zero further wiring.
 */
class BundleTierSelector extends HTMLElement {
  #variants = [];

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    try {
      this.#variants = JSON.parse(this.dataset.variants || '[]');
    } catch (error) {
      console.error('[bundle-tier-selector] Could not parse product variants.', error);
    }

    this.#syncDefaults();

    const checked = this.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) {
      this.#applyQuantity(checked);
    }

    this.#updateButtonPrice();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'radio' && target.name.startsWith('bundle-selector-tier-')) {
      this.#applyQuantity(target);
      this.#updateTier(target.closest('.bundle-tier'));
      this.#updateButtonPrice();
    } else if (target instanceof HTMLSelectElement && target.matches('.bundle-tier__variant-select')) {
      this.#updateSwatch(target);
      this.#updateTier(target.closest('.bundle-tier'));
      this.#updateButtonPrice();
    }
  };

  /** @param {HTMLInputElement} radio */
  #applyQuantity(radio) {
    const quantity = radio.dataset.quantity || radio.value || '1';
    const scope = this.closest('.product-details') || document;
    const quantitySelector = scope.querySelector('quantity-selector-component');

    if (quantitySelector && typeof (/** @type {any} */ (quantitySelector).setValue) === 'function') {
      /** @type {any} */ (quantitySelector).setValue(quantity);
    }
  }

  #syncDefaults() {
    for (const select of this.querySelectorAll('.bundle-tier__variant-select')) {
      this.#updateSwatch(select);
    }
    for (const tier of this.querySelectorAll('.bundle-tier')) this.#updateTier(tier);
  }

  #variantForSelect(select) {
    const optionIndex = Number(select.dataset.optionPosition) - 1;
    const defaultVariant = this.#variants.find((variant) => String(variant.id) === this.dataset.defaultVariantId);
    const defaultOptions = defaultVariant?.options || [];
    return (
      this.#variants.find(
        (variant) =>
          variant.available &&
          variant.options?.[optionIndex] === select.value &&
          variant.options.every((value, index) => index === optionIndex || value === defaultOptions[index])
      ) || this.#variants.find((variant) => variant.available && variant.options?.[optionIndex] === select.value)
    );
  }

  #updateSwatch(select) {
    const swatch = select.closest('.bundle-tier__variant')?.querySelector('.bundle-tier__swatch');
    const option = select.selectedOptions[0];
    if (!swatch || !option) return;
    const basicColor = { 'jet black': '#282828', black: '#282828', 'blush pink': '#ef9ead', pink: '#ef9ead', 'snow white': '#eee', white: '#eee', 'ocean blue': '#37b6d3', blue: '#37b6d3' };
    swatch.style.backgroundColor = option.dataset.swatchColor || basicColor[option.value.toLowerCase()] || '#d9dedb';
    swatch.style.backgroundImage = option.dataset.swatchImage ? `url("${option.dataset.swatchImage}")` : 'none';
  }

  #getSelectedVariants(tier) {
    const selects = Array.from(tier.querySelectorAll('.bundle-tier__variant-select'));
    if (!selects.length) {
      const variant = this.#variants.find((item) => String(item.id) === this.dataset.defaultVariantId) || this.#variants.find((item) => item.available);
      return variant ? Array(Number(tier.dataset.totalQuantity) || 1).fill(variant) : [];
    }
    return selects.map((select) => this.#variantForSelect(select));
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${this.dataset.currency || 'EUR'}`;
    }
  }

  #updateTier(tier) {
    if (!tier) return;
    const variants = this.#getSelectedVariants(tier);
    if (!variants.length || variants.some((variant) => !variant)) return;
    const configuredCompare = Number(tier.dataset.comparePrice);
    const compareCents = configuredCompare || variants.reduce((sum, variant) => {
      const compareAt = Number(variant.compare_at_price) || Number(variant.price);
      return sum + Math.max(compareAt, Number(variant.price));
    }, 0);
    const compare = tier.querySelector('[data-bundle-compare]');
    if (compare) compare.textContent = this.#formatMoney(compareCents);
  }

  #updateButtonPrice() {
    const selectedTier = this.querySelector('.bundle-tier:has(input[type="radio"]:checked)');
    const button = this.closest('.product-details')?.querySelector('product-form-component .add-to-cart-button');
    if (!button) return;

    let priceDisplay = button.querySelector('.bundle-selector__button-prices');
    if (!selectedTier) {
      priceDisplay?.remove();
      return;
    }

    const displayPrice = Number(selectedTier.dataset.displayPrice) || 0;
    const variants = this.#getSelectedVariants(selectedTier);
    if (!displayPrice) return;
    const configuredCompare = Number(selectedTier.dataset.comparePrice);
    const compareCents = configuredCompare || variants.reduce((sum, variant) => {
      if (!variant) return sum;
      return sum + Math.max(Number(variant.compare_at_price) || Number(variant.price), Number(variant.price));
    }, 0);

    if (!priceDisplay) {
      priceDisplay = document.createElement('span');
      priceDisplay.className = 'bundle-selector__button-prices';
      priceDisplay.innerHTML = '<s class="bundle-selector__button-compare"></s><span class="bundle-selector__button-current"></span>';
      button.append(priceDisplay);
    }
    const compare = priceDisplay.querySelector('.bundle-selector__button-compare');
    const current = priceDisplay.querySelector('.bundle-selector__button-current');
    if (compare) {
      compare.textContent = compareCents > displayPrice ? this.#formatMoney(compareCents) : '';
      compare.hidden = compareCents <= displayPrice;
    }
    if (current) current.textContent = this.#formatMoney(displayPrice);
  }

  /** @returns {Array<{variantId: string, quantity: number}> | null} */
  getSelectedItems() {
    const checked = this.querySelector('input[type="radio"]:checked');
    const tier = checked?.closest('.bundle-tier');
    if (!tier) return null;
    const variants = this.#getSelectedVariants(tier);
    if (!variants.length || variants.some((variant) => !variant)) return [];

    const counts = new Map();
    for (const variant of variants) {
      const variantId = String(variant.id);
      counts.set(variantId, (counts.get(variantId) || 0) + 1);
    }
    return Array.from(counts, ([variantId, quantity]) => ({ variantId, quantity }));
  }
}

if (!customElements.get('bundle-tier-selector')) {
  customElements.define('bundle-tier-selector', BundleTierSelector);
}
